import { fontInter } from '@m/styled/fonts';

import { createTheme, defineThemeOverride } from './create-theme';

/**
 * Luxury theme (ivory / jade palette).
 * Semantic tokens from Figma "Luxury - Light" mode.
 *
 * Ivory/000 #FCFAF5 · Ivory/100 #F1EDE5 · Ivory/200 #EAE5DB · Ivory/300 #DED8CC
 * Ivory/400 #CDC6B8 · Ivory/500 #9B9589 · Ivory/600 #6E6960 · Ivory/700 #45423D
 * Ivory/800 #2B2926 · Ivory/900 #1A1917
 * Jade/000 #CCD0CC · Jade/100 #ADB5AD · Jade/200 #8F9B8F · Jade/300 #6F7A6F
 * Jade/400 #4B574B · Jade/500 #424D42 · Jade/600 #3D483D · Jade/700 #333D33
 *
 * Dark values mirror default gray shade numbers on the ivory scale (no Luxury Dark
 * export yet). Accent / brand (blurple) tokens stay on the base theme, except
 * Switch On fills which use jade in Luxury Light.
 */
/** Override config — also used by the Semantic Tokens Storybook matrix. */
export const luxuryThemeConfig = defineThemeOverride({
  theme: {
    tokens: {
      fonts: {
        body: { value: fontInter },
        heading: { value: fontInter },
      },
      colors: {
        ivory: {
          '000': { value: '#FCFAF5' },
          100: { value: '#F1EDE5' },
          200: { value: '#EAE5DB' },
          300: { value: '#DED8CC' },
          400: { value: '#CDC6B8' },
          500: { value: '#9B9589' },
          600: { value: '#6E6960' },
          700: { value: '#45423D' },
          800: { value: '#2B2926' },
          900: { value: '#1A1917' },
        },
        jade: {
          '000': { value: '#CCD0CC' },
          100: { value: '#ADB5AD' },
          200: { value: '#8F9B8F' },
          300: { value: '#6F7A6F' },
          400: { value: '#4B574B' },
          500: { value: '#424D42' },
          600: { value: '#3D483D' },
          700: { value: '#333D33' },
        },
      },
    },
    semanticTokens: {
      colors: {
        border: {
          subtle: {
            value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.600}' },
          },
        },
        text: {
          primary: {
            value: { _light: '{colors.ivory.900}', _dark: '{colors.ivory.000}' },
          },
          secondary: {
            value: { _light: '{colors.ivory.700}', _dark: '{colors.ivory.300}' },
          },
          tertiary: {
            value: { _light: '{colors.ivory.600}', _dark: '{colors.ivory.400}' },
          },
          'read-only': {
            value: { _light: '{colors.ivory.800}', _dark: '{colors.ivory.200}' },
          },
          disabled: {
            value: { _light: '{colors.ivory.600}', _dark: '{colors.ivory.600}' },
          },
          link: {
            enabled: {
              value: { _light: '{colors.ivory.900}', _dark: '{colors.ivory.000}' },
            },
            hover: {
              value: { _light: '{colors.ivory.800}', _dark: '{colors.ivory.100}' },
            },
            active: {
              value: { _light: '{colors.ivory.700}', _dark: '{colors.ivory.200}' },
            },
            focused: {
              value: { _light: '{colors.ivory.900}', _dark: '{colors.ivory.000}' },
            },
            disabled: {
              value: { _light: '{colors.ivory.600}', _dark: '{colors.ivory.400}' },
            },
          },
          'destructive-secondary': {
            disabled: {
              value: { _light: '{colors.ivory.600}', _dark: '{colors.ivory.400}' },
            },
          },
        },
        fill: {
          icon: {
            interactive: {
              value: { _light: '{colors.ivory.900}', _dark: '{colors.ivory.000}' },
            },
            descriptive: {
              value: { _light: '{colors.ivory.700}', _dark: '{colors.ivory.300}' },
            },
            disabled: {
              value: { _light: '{colors.ivory.600}', _dark: '{colors.ivory.600}' },
            },
            hover: {
              value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
            },
            active: {
              value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.600}' },
            },
          },
          background: {
            default: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.800}' },
            },
            'light-gray': {
              value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.900}' },
            },
          },
          badge: {
            neutral: {
              value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.600}' },
            },
          },
          interactive: {
            enabled: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            hover: {
              value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.700}' },
            },
            selected: {
              value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
            },
            'hover-selected': {
              value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.600}' },
            },
          },
          input: {
            enabled: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.700}' },
            },
            hover: {
              value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.800}' },
            },
            active: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            focused: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            'read-only': {
              value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.700}' },
            },
            disabled: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            error: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            filled: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
          },
          button: {
            primary: {
              enabled: {
                value: { _light: '{colors.jade.700}', _dark: '{colors.ivory.000}' },
              },
              hover: {
                value: { _light: '{colors.jade.600}', _dark: '{colors.ivory.300}' },
              },
              active: {
                value: { _light: '{colors.jade.500}', _dark: '{colors.ivory.300}' },
              },
              focused: {
                value: { _light: '{colors.jade.700}', _dark: '{colors.ivory.000}' },
              },
              selected: {
                value: { _light: '{colors.jade.700}', _dark: '{colors.ivory.000}' },
              },
              disabled: {
                value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.800}' },
              },
            },
            secondary: {
              enabled: {
                value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
              },
              hover: {
                value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.600}' },
              },
              active: {
                value: { _light: '{colors.ivory.400}', _dark: '{colors.ivory.500}' },
              },
              focused: {
                value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
              },
              selected: {
                value: { _light: '{colors.ivory.700}', _dark: '{colors.ivory.200}' },
              },
              disabled: {
                value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.800}' },
              },
            },
            tertiary: {
              enabled: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
              hover: {
                value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.700}' },
              },
              active: {
                value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.600}' },
              },
              focused: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
              selected: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
              disabled: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
            },
            'destructive-primary': {
              disabled: {
                value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.800}' },
              },
            },
            'destructive-secondary': {
              enabled: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
              focused: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
              selected: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
              disabled: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
            },
            navigation: {
              enabled: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
              hover: {
                value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.700}' },
              },
              active: {
                value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.600}' },
              },
              focused: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
              selected: {
                value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
              },
              disabled: {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
              },
            },
            'segmented-tab': {
              'track-background': {
                value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.900}' },
              },
              'active-background': {
                value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.700}' },
              },
            },
          },
          'day-picker': {
            enabled: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            hover: {
              value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.700}' },
            },
            active: {
              value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.600}' },
            },
            focused: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            selected: {
              value: { _light: '{colors.ivory.700}', _dark: '{colors.ivory.200}' },
            },
            range: {
              value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.600}' },
            },
            today: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            disabled: {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
          },
          switch: {
            thumb: {
              on: {
                enabled: {
                  value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.000}' },
                },
                hover: {
                  value: { _light: '{colors.jade.000}', _dark: '{colors.ivory.200}' },
                },
                active: {
                  value: { _light: '{colors.jade.100}', _dark: '{colors.ivory.300}' },
                },
                focused: {
                  value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.000}' },
                },
                'read-only': {
                  value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.500}' },
                },
                disabled: {
                  value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.600}' },
                },
              },
              off: {
                enabled: {
                  value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.200}' },
                },
                hover: {
                  value: { _light: '{colors.ivory.100}', _dark: '{colors.ivory.400}' },
                },
                active: {
                  value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.500}' },
                },
                focused: {
                  value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.200}' },
                },
                'read-only': {
                  value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.500}' },
                },
                disabled: {
                  value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.600}' },
                },
              },
            },
            track: {
              on: {
                enabled: {
                  value: { _light: '{colors.jade.700}', _dark: '{colors.ivory.000}' },
                },
                hover: {
                  value: { _light: '{colors.jade.700}', _dark: '{colors.ivory.000}' },
                },
                active: {
                  value: { _light: '{colors.jade.700}', _dark: '{colors.ivory.000}' },
                },
                focused: {
                  value: { _light: '{colors.jade.700}', _dark: '{colors.ivory.000}' },
                },
                'read-only': {
                  value: { _light: '{colors.jade.300}', _dark: '{colors.ivory.600}' },
                },
                disabled: {
                  value: { _light: '{colors.jade.100}', _dark: '{colors.ivory.700}' },
                },
              },
              off: {
                enabled: {
                  value: { _light: '{colors.ivory.500}', _dark: '{colors.ivory.600}' },
                },
                hover: {
                  value: { _light: '{colors.ivory.500}', _dark: '{colors.ivory.600}' },
                },
                active: {
                  value: { _light: '{colors.ivory.500}', _dark: '{colors.ivory.600}' },
                },
                focused: {
                  value: { _light: '{colors.ivory.500}', _dark: '{colors.ivory.600}' },
                },
                'read-only': {
                  value: { _light: '{colors.ivory.400}', _dark: '{colors.ivory.700}' },
                },
                disabled: {
                  value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.700}' },
                },
              },
            },
          },
        },
        stroke: {
          border: {
            input: {
              value: { _light: '{colors.ivory.500}', _dark: '{colors.ivory.400}' },
            },
            disabled: {
              value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.600}' },
            },
            focused: {
              value: { _light: '{colors.ivory.900}', _dark: '{colors.ivory.000}' },
            },
            'focused-wrapper': {
              value: { _light: '{colors.ivory.000}', _dark: '{colors.ivory.900}' },
            },
            hover: {
              value: { _light: '{colors.ivory.600}', _dark: '{colors.ivory.300}' },
            },
            container: {
              value: { _light: '{colors.ivory.300}', _dark: '{colors.ivory.600}' },
            },
          },
          button: {
            tertiary: {
              enabled: {
                value: { _light: '{colors.ivory.500}', _dark: '{colors.ivory.400}' },
              },
              hover: {
                value: { _light: '{colors.ivory.500}', _dark: '{colors.ivory.400}' },
              },
              active: {
                value: { _light: '{colors.ivory.600}', _dark: '{colors.ivory.300}' },
              },
              focused: {
                value: { _light: '{colors.ivory.900}', _dark: '{colors.ivory.000}' },
              },
              selected: {
                value: { _light: '{colors.ivory.900}', _dark: '{colors.ivory.000}' },
              },
              disabled: {
                value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
              },
            },
            'destructive-secondary': {
              disabled: {
                value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
              },
            },
          },
          divider: {
            default: {
              value: { _light: '{colors.ivory.200}', _dark: '{colors.ivory.700}' },
            },
            emphasized: {
              value: { _light: '{colors.ivory.400}', _dark: '{colors.ivory.600}' },
            },
          },
        },
      },
    },
  },
});

export const luxurySystem = createTheme(luxuryThemeConfig);
